// eslint-disable formatjs/no-literal-string-in-jsx
import React from 'react';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { MatrixProduct } from '../composer/blocks/MatrixColumns';
import { Deal, Plan } from '../../types/plans';
import { useIntl, defineMessages } from 'react-intl';
import {
  getProductPlan,
  getUpsellProductPlan,
} from '../../lib/composer/blocks/matrixHelpers';
import { MatrixButtonBlock } from './MatrixButtonBlock';
import { INFO_SIGN } from '@elsevier-health/osmosis-shared/constants';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faInfo } from '@fortawesome/pro-light-svg-icons/faInfo';
import { P } from '@osmosis/tailwind-ui/components/Text';

type Props = {
  products?: MatrixProduct[];
  plans: Plan[];
  deal?: Deal;
  activePlanLength: number;
  productFeatures: {
    title:
      | string
      | { id: string; defaultMessage: string }
      | ((
          product: MatrixProduct,
        ) => string | { id: string; defaultMessage: string });
    tooltipText?: string;
    products: (MatrixProduct & { enabled?: boolean })[];
  }[];
  monthlyDailyPrice: string | null;
  footerText?: string;
};

/**
 * Return Matrix Plan Selector component.
 */
const renderHeader = (
  products: MatrixProduct[],
  plans: Plan[],
  activePlanLength: number,
  monthlyDailyPrice: string | null,
  colorMap: (index: number) => string,
  intl: any,
  deal = {} as Deal,
) => {
  return (
    <>
      <tr>
        <th
          data-testid={`matrix-plan-grid`}
          className="pb-2 relative border-b"
          style={{ borderBottomColor: 'black' }}
          scope="col"
          id="whats-in-package"
          rowSpan={2}
        >
          <div className="absolute bottom-4 font-bold">
            {intl.formatMessage({
              id: 'matrix.whats_in_the_package',
              defaultMessage: "What's in the package?",
            })}
          </div>
        </th>

        {products.map(({ name, productID, displayDescription }, index) => {
          const plan = getProductPlan(productID, plans, activePlanLength);
          const headerId = `${name.toLowerCase().replace(/\s+/g, '-')}`;
          const colorMap: { [key: number]: string } = {
            0: 'text-green-700',
            1: 'text-blue-700',
          };
          const color = colorMap[index];

          return (
            <React.Fragment key={productID}>
              <th
                className={`${color} text-center font-bold border-gray-200 pt-6`}
                style={{
                  fontSize: '1.25rem',
                  border: '1px solid lightgray',
                  borderBottom: '1px solid white',
                }}
                data-testid={`matrix-plan-title-${plan?.code}`}
                scope="col"
                id={headerId}
              >
                {name}
              </th>
            </React.Fragment>
          );
        })}
      </tr>
      <tr>
        {products.map(
          ({ name, originName, productID, displayDescription }, index) => {
            const plan = getProductPlan(productID, plans, activePlanLength);
            const upsellPlan = getUpsellProductPlan(plans, plan);
            const headerId = `${name.toLowerCase().replace(/\s+/g, '-')}`;
            return (
              <td
                key={productID}
                headers={headerId}
                id={`buy-${headerId}`}
                className="border-none"
                style={{
                  borderRight: '1px solid lightgray',
                  borderLeft: '1px solid lightgray',
                }}
              >
                <MatrixButtonBlock
                  name={name}
                  originName={originName || name}
                  color={colorMap(index)}
                  deal={deal}
                  position="top"
                  plan={plan}
                  upsellPlan={upsellPlan}
                  displayDescription={displayDescription}
                  monthlyDailyPrice={monthlyDailyPrice}
                />
              </td>
            );
          },
        )}
      </tr>
    </>
  );
};

interface ProductFeature {
  title:
    | string
    | { id: string; defaultMessage: string }
    | ((
        product: MatrixProduct,
      ) => string | { id: string; defaultMessage: string });
  tooltipText?: string;
  products: (MatrixProduct & { enabled?: boolean })[];
}

interface RenderBodyProps {
  productFeatures: ProductFeature[];
  intl: any;
  colorMap: (index: number) => string;
}

const renderBody = ({ productFeatures, intl, colorMap }: RenderBodyProps) => {
  return productFeatures.map((item, i) => {
    const title =
      typeof item.title === 'function'
        ? item.title(item.products[0])
        : item.title;

    const titleStr =
      typeof title === 'string' ? title : intl.formatMessage(title);
    const formattedTitle = titleStr.replace(INFO_SIGN, '');
    const keyId = typeof title === 'string' ? title : title?.id;
    //grab the first 3 words from this string and remove spaces in between
    const id = (keyId as string).split(' ').slice(0, 3).join('');
    const tooltipId = `feature-tooltip-${id}`;
    const messageId = `matrix.tooltip.${tooltipId}`;
    const message = defineMessages({
      existing: {
        id: messageId,
        defaultMessage: item.tooltipText,
      },
    });
    const ariaLabel = intl.formatMessage(message.existing);

    return (
      <tr key={keyId}>
        <th
          className="border-b border-gray-200 p-4 pr-8 font-normal text-left"
          headers="whats-in-package" // refers to td1 id
          scope="row"
        >
          {formattedTitle}
          {item.tooltipText && (
            <span>
              <Icon
                tabIndex={0}
                icon={faInfo}
                data-tooltip-id={tooltipId}
                size="xs"
                className="hover:text-white hover:bg-black rounded-full border-1 border-black w-3 !h-3 p-[2px] relative top-[4px]"
                aria-label={ariaLabel}
                role="button"
              />
              <Tooltip
                id={tooltipId}
                content={item.tooltipText}
                role="tooltip"
                clickable
                globalCloseEvents={{ escape: true }}
              />
            </span>
          )}
        </th>

        {item.products.map(({ name, color, enabled }, index) => {
          const headerId = `${name.toLowerCase().replace(/\s+/g, '-')}`;
          return (
            <React.Fragment key={name}>
              <td
                className="border-b border-gray-200 p-2 text-center border-t-black"
                headers={headerId} //refers to td2 id
              >
                {enabled ? (
                  <div
                    className={`bg-${colorMap(
                      index,
                    )} text-white rounded-full h-[17px] w-[17px] m-auto relative`}
                    role="img"
                    aria-label={intl.formatMessage(
                      {
                        id: 'plans.feature.checkmark',
                        defaultMessage: 'Included in {plan} checkmark',
                      },
                      { plan: name },
                    )}
                  >
                    <div className="pb-4">
                      <Icon
                        icon={faCheck}
                        style={{
                          fontSize: '10px',
                          position: 'relative',
                          bottom: '5px',
                        }}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                ) : (
                  <span className="sr-only">
                    {intl.formatMessage({
                      id: 'plans.feature.not_included',
                      defaultMessage: 'Not included',
                    })}
                  </span>
                )}
              </td>
            </React.Fragment>
          );
        })}
      </tr>
    );
  });
};

const renderFooter = (
  products: MatrixProduct[],
  plans: Plan[],
  activePlanLength: number,
  monthlyDailyPrice: string | null,
  colorMap: (index: number) => string,
  deal: Deal = {} as Deal,
) => {
  return products.map(
    ({ name, color = '#376D37', productID, displayDescription }, index) => {
      const plan = getProductPlan(productID, plans, activePlanLength);
      const upsellPlan = getUpsellProductPlan(plans, plan);
      const headerId = name.toLowerCase().replace(/\s+/g, '-');

      return (
        <React.Fragment key={productID}>
          <td
            className="pt-4 pb-2 border-gray-200"
            headers={`purchase-row buy-${headerId}`}
          >
            <MatrixButtonBlock
              name={name}
              color={colorMap(index)}
              deal={deal}
              position="bottom"
              plan={plan}
              upsellPlan={upsellPlan}
              displayDescription={displayDescription}
              monthlyDailyPrice={monthlyDailyPrice}
            />
          </td>
        </React.Fragment>
      );
    },
  );
};

/**
 * MatrixPlanSelector component renders a comparison table of different plans and their features.
 * It supports both large and small screen sizes by rendering different table structures.
 */
const MatrixPlanSelector = ({
  products: _products = [],
  plans = [],
  deal,
  activePlanLength,
  productFeatures,
  monthlyDailyPrice,
  footerText = '',
}: Props) => {
  const products = _products.filter(({ productID }) =>
    getProductPlan(productID, plans, activePlanLength),
  );

  const intl = useIntl();

  const colorMap = (index: number) => (index === 1 ? 'blue-700' : 'green-700');

  // Utility function to remove parentheses and their contents, and everything that follows
  const removeParensAndSpecialChars = (str: string): string => {
    let inParens = false;

    const result = Array.from(str).reduce((acc, char) => {
      if (char === '(') {
        inParens = true;
      } else if (char === ')') {
        inParens = false;
      } else if (!inParens) {
        acc += char;
      }
      return acc;
    }, '');

    return result
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
  };

  return (
    <div className="lg:max-w-[1020px] md:p-4 mx-auto">
      <div className="overflow-auto w-full" role="region">
        <table
          className="hidden md:table min-w-full bg-white"
          data-testid="table-wrapper"
          id="matrix-plan-table"
          style={{ borderCollapse: 'collapse' }}
        >
          <caption id="matrix-plan-description" className="sr-only">
            {intl.formatMessage({
              id: 'matrix.plan.description',
              defaultMessage:
                'A comparison table of different plans and their features.',
            })}
          </caption>
          <thead>
            {renderHeader(
              products,
              plans,
              activePlanLength,
              monthlyDailyPrice,
              colorMap,
              intl,
              deal,
            )}
          </thead>
          <tbody>{renderBody({ productFeatures, intl, colorMap })}</tbody>
          <tfoot>
            <tr>
              <th id="purchase-row" className="sr-only" scope="row">
                {intl.formatMessage({
                  id: 'matrix.footer.screenreader',
                  defaultMessage: 'Buy Now Buttons',
                })}
              </th>
              {renderFooter(
                products,
                plans,
                activePlanLength,
                monthlyDailyPrice,
                colorMap,
                deal,
              )}
            </tr>
          </tfoot>
        </table>

        {/* small size */}
        <table
          className="md:hidden min-w-full bg-white"
          data-testid="table-wrapper"
          id="matrix-plan-table"
        >
          <caption id="matrix-plan-description" className="sr-only">
            {intl.formatMessage({
              id: 'matrix.plan.description',
              defaultMessage:
                'A comparison table of different plans and their features.',
            })}
          </caption>
          <thead>
            <tr>
              {products.map(
                ({ name, productID, displayDescription }, index) => {
                  const plan = getProductPlan(
                    productID,
                    plans,
                    activePlanLength,
                  );
                  const headerId = `${name.toLowerCase().replace(/\s+/g, '-')}`;
                  const colorMap: { [key: number]: string } = {
                    0: 'text-green-700',
                    1: 'text-blue-700',
                  };
                  const color = colorMap[index];

                  return (
                    <React.Fragment key={productID}>
                      <th
                        className={`${color} text-center font-bold border-none pt-5`}
                        style={{ fontSize: '18px' }}
                        data-testid={`matrix-plan-title-${plan?.code}`}
                        scope="col"
                        id={headerId}
                      >
                        {name}
                      </th>
                    </React.Fragment>
                  );
                },
              )}
            </tr>
            <tr>
              {products.map(
                (
                  { name, color = '#376D37', productID, displayDescription },
                  index,
                ) => {
                  const plan = getProductPlan(
                    productID,
                    plans,
                    activePlanLength,
                  );
                  const upsellPlan = getUpsellProductPlan(plans, plan);
                  const headerId = `${name.toLowerCase().replace(/\s+/g, '-')}`;
                  return (
                    <td
                      key={productID}
                      className="pb-0 border-none"
                      headers={headerId}
                      id={`buy-${headerId}`}
                    >
                      <MatrixButtonBlock
                        name={name}
                        color={colorMap(index)}
                        deal={deal}
                        position="top"
                        upsellPlan={upsellPlan}
                        plan={plan}
                        displayDescription={displayDescription}
                        monthlyDailyPrice={monthlyDailyPrice}
                        size={'small'}
                      />
                    </td>
                  );
                },
              )}
            </tr>
          </thead>
          <tbody className="no-border">
            {productFeatures.map((item, i) => {
              const title =
                typeof item.title === 'function'
                  ? item.title(item.products[0])
                  : item.title;

              const titleStr =
                typeof title === 'string' ? title : intl.formatMessage(title);
              const formattedTitle = titleStr.replace(INFO_SIGN, '');
              const keyId = typeof title === 'string' ? title : title.id;
              const tooltipId = `feature-tooltip-${keyId}`;
              const tdBorder = { border: '0px solid white' };
              const headerId = removeParensAndSpecialChars(titleStr);
              return (
                <React.Fragment key={keyId}>
                  <tr>
                    <th
                      className="bg-gray-200 border-b border-gray-100 p-4 overflow-hidden mx-auto"
                      colSpan={100}
                      id={headerId}
                      style={{ textAlign: 'center' }}
                    >
                      {formattedTitle}
                      {item.tooltipText && (
                        <span>
                          <Icon
                            tabIndex={0}
                            icon={faInfo}
                            data-tooltip-id={tooltipId}
                            size="xs"
                            className="hover:text-white hover:bg-black rounded-full border border-black w-3 h-3 p-[2px] relative top-[4px]"
                            aria-label={intl.formatMessage({
                              id: 'matrix.tooltip_info',
                              defaultMessage: 'More information',
                            })}
                          />
                          <Tooltip
                            id={tooltipId}
                            content={item.tooltipText}
                            style={{ maxWidth: '300px' }}
                            clickable
                            globalCloseEvents={{ escape: true }}
                          />
                        </span>
                      )}
                    </th>
                  </tr>
                  <tr>
                    {item.products.map(({ name, color, enabled }, index) => {
                      return (
                        <td
                          className="p-2 text-center pt-4 pb-4"
                          style={tdBorder}
                          key={name}
                          headers={headerId}
                        >
                          {item.products[index].enabled ? (
                            <div
                              className={`bg-${colorMap(
                                index,
                              )} text-white bg-black rounded-full h-[17px] w-[17px] m-auto relative`}
                              role="img"
                              aria-label={intl.formatMessage(
                                {
                                  id: 'plans.feature.checkmark',
                                  defaultMessage:
                                    'Included in {plan} checkmark',
                                },
                                { plan: name },
                              )}
                            >
                              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                <Icon
                                  icon={faCheck}
                                  style={{
                                    fontSize: '10px',
                                    position: 'relative',
                                    bottom: '2px',
                                  }}
                                  aria-hidden="true"
                                />
                              </div>
                            </div>
                          ) : (
                            <span className="sr-only">
                              {intl.formatMessage({
                                id: 'plans.feature.not_included',
                                defaultMessage: 'Not included',
                              })}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              {products.map(
                (
                  { name, color = '#376D37', productID, displayDescription },
                  index,
                ) => {
                  const plan = getProductPlan(
                    productID,
                    plans,
                    activePlanLength,
                  );
                  const upsellPlan = getUpsellProductPlan(plans, plan);
                  const headerId = `${name.toLowerCase().replace(/\s+/g, '-')}`;
                  return (
                    <React.Fragment key={productID}>
                      <th
                        className="pt-4 pb-2"
                        headers={`${headerId} buy-${headerId}`}
                      >
                        <MatrixButtonBlock
                          name={name}
                          color={colorMap(index)}
                          deal={deal}
                          position="bottom"
                          plan={plan}
                          upsellPlan={upsellPlan}
                          displayDescription={displayDescription}
                          monthlyDailyPrice={monthlyDailyPrice}
                        />
                      </th>
                    </React.Fragment>
                  );
                },
              )}
            </tr>
          </tfoot>
        </table>
        {!!footerText && (
          <P className={'text-center text-gray-600 mt-4'}>{footerText}</P>
        )}
      </div>
    </div>
  );
};

export default MatrixPlanSelector;
